/**
 * Live swap: USDG → stock token, same-chain on Robinhood Chain (4663) via
 * LiFi's on-chain routing (Uniswap v3/v4 pools underneath). Executes ON 4663,
 * so the wallet needs a dust of ETH there — maintained by the gas manager,
 * paid out of the round-up batch (owner decision, see config.DEST_GAS).
 */
import { parseEventLogs, erc20Abi } from "viem";
import { LIFI, ROBINHOOD_CHAIN } from "../config";
import { robinhoodPublicClient } from "../chains";
import { getLifiQuote, sendLifiTransaction, LifiError, type LifiQuote } from "../lifi";
import type { LocalWallet } from "../wallet";
import type { Hex } from "../types";
import type { SwapAdapter, SwapQuote, SwapResult } from "./types";

export class LifiSwap implements SwapAdapter {
  name = "lifi-robinhood-dex";
  private pending = new Map<string, LifiQuote>();

  constructor(private wallet: LocalWallet) {}

  async getQuote(stockSymbol: string, inputUnits: bigint): Promise<SwapQuote> {
    const stock = ROBINHOOD_CHAIN.stockTokens[stockSymbol];
    if (!stock) throw new LifiError(`Unknown stock token: ${stockSymbol}`, "no_route");
    const quote = await getLifiQuote({
      fromChain: ROBINHOOD_CHAIN.id,
      toChain: ROBINHOOD_CHAIN.id,
      fromToken: ROBINHOOD_CHAIN.usdg.address,
      toToken: stock.address,
      fromAmount: inputUnits,
      fromAddress: this.wallet.address,
      slippage: LIFI.swapSlippage,
    });
    const quoteId = `lifi-swap-${Date.now()}-${inputUnits}`;
    this.pending.set(quoteId, quote);
    return {
      stockSymbol,
      inputUnits,
      expectedStockUnits: quote.toAmount,
      // USDG-denominated estimate of route cost; actual is measured on-chain.
      feeUnits: BigInt(quote.feeUsdE6),
      quoteId,
      expiresAt: Date.now() + 60_000,
    };
  }

  async submit(quote: SwapQuote): Promise<Hex> {
    const lifi = this.pending.get(quote.quoteId);
    if (!lifi) throw new LifiError("Swap quote expired — requote needed", "failed");
    this.pending.delete(quote.quoteId);
    return sendLifiTransaction(this.wallet.account, lifi);
  }

  /** Wait for the receipt and measure what actually arrived — recovery-safe. */
  async resolve(txHash: Hex, stockSymbol: string, fallbackExpected?: bigint): Promise<SwapResult> {
    const client = robinhoodPublicClient();
    const receipt = await client.waitForTransactionReceipt({ hash: txHash, timeout: LIFI.statusTimeoutMs });
    if (receipt.status !== "success") {
      throw new LifiError(`Swap transaction reverted (${txHash})`, "failed");
    }

    // Ground truth: the stock-token Transfer into our wallet in this receipt.
    const stock = ROBINHOOD_CHAIN.stockTokens[stockSymbol];
    const transfers = parseEventLogs({ abi: erc20Abi, logs: receipt.logs, eventName: "Transfer" });
    const received = transfers
      .filter(
        (t) =>
          t.address.toLowerCase() === stock.address.toLowerCase() &&
          t.args.to.toLowerCase() === this.wallet.address.toLowerCase(),
      )
      .reduce((s, t) => s + t.args.value, 0n);

    return { txHash, stockUnitsReceived: received > 0n ? received : (fallbackExpected ?? 0n) };
  }
}
